/**
 * SignUpForm
 *
 * Controlled sign-up form with first/last name, email, password fields,
 * password visibility toggles, and terms acceptance.
 */
import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { ButtonSpinner } from '../../ui/feedback/loading-spinner.jsx';
import { Link } from 'react-router-dom';
import PhoneInput from '../../ui/inputs/PhoneInput';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../ui/inputs/select';
import { Label } from '../../ui/inputs/label';
import { getRolesForSelect } from '../../../data/roles/politicalRoles';

export default function SignUpForm({ onSubmit, isLoading, onInputChange }) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [role, setRole] = useState('');
  const [otherRole, setOtherRole] = useState('');
  const [phone, setPhone] = useState({ e164: '', country: 'US', national: '' });
  const [phoneError, setPhoneError] = useState('');
  const [roleError, setRoleError] = useState('');
  
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  
  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // Handle submit with loading state and terms validation
  const handleSubmit = (e) => {
    if (isLoading) {
      e.preventDefault();
      return;
    }
    
    // Check if user agreed to terms
    if (!agreedToTerms) {
      e.preventDefault();
      alert('Please accept the Terms of Service and Privacy Policy to continue.');
      return;
    }
    
    // Validate phone - require 10 US digits
    const digits = String(phone?.national || '').replace(/[^0-9]/g, '');
    if (digits.length !== 10) {
      e.preventDefault();
      setPhoneError('Enter a valid 10-digit US phone number.');
      return;
    }

    // Validate role - require a selection, and require text when 'Other'
    const roleValue = role === 'Other' ? (otherRole || '').trim() : (role || '').trim();
    if (!roleValue) {
      e.preventDefault();
      setRoleError('Please select your role.');
      return;
    }

    // Inject role/phone hidden inputs for submit handler to read
    const form = e.target;
    const ensureHidden = (name, value) => {
      let el = form.querySelector(`input[name="${name}"]`);
      if (!el) {
        el = document.createElement('input');
        el.type = 'hidden';
        el.name = name;
        form.appendChild(el);
      }
      el.value = value ?? '';
    };
    ensureHidden('role', role === 'Other' ? otherRole : role);
    // Phone hidden inputs are rendered inside PhoneInput; don't override them here
    onSubmit(e);
  };

  return (
    <div className="mx-auto w-[95%] md:w-[85%] max-w-[600px] px-6 py-8 mt-6 sm:px-8 bg-white dark:bg-gray-800/50 backdrop-blur-sm border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg">
      <form className="space-y-6" onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label htmlFor="first-name" className="block text-sm font-semibold leading-6 text-gray-900 dark:text-white">
              First name
            </label>
            <div className="mt-2">
              <input
                id="first-name"
                name="first-name"
                type="text"
                autoComplete="given-name"
                required
                onInput={onInputChange}
                placeholder="Enter first name"
                className="block w-full rounded-md bg-white dark:bg-gray-700/80 border border-gray-300 dark:border-gray-600 py-2 px-3.5 text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-500 sm:text-sm sm:leading-6"
              />
            </div>
          </div>
          
          <div>
            <label htmlFor="last-name" className="block text-sm font-semibold leading-6 text-gray-900 dark:text-white">
              Last name
            </label>
            <div className="mt-2">
              <input
                id="last-name"
                name="last-name"
                type="text"
                autoComplete="family-name"
                required
                onInput={onInputChange}
                placeholder="Enter last name"
                className="block w-full rounded-md bg-white dark:bg-gray-700/80 border border-gray-300 dark:border-gray-600 py-2 px-3.5 text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-500 sm:text-sm sm:leading-6"
              />
            </div>
          </div>
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-semibold leading-6 text-gray-900 dark:text-white">
            Email address
          </label>
          <div className="mt-2">
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              onInput={onInputChange}
              placeholder="Enter email address"
              className="block w-full rounded-md bg-white dark:bg-gray-700/80 border border-gray-300 dark:border-gray-600 py-2 px-3.5 text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-500 sm:text-sm sm:leading-6"
            />
          </div>
        </div>

        {/* Phone and Role in two columns */}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <PhoneInput
              id="phone"
              name="phone"
              label="Phone number"
              required={true}
              defaultCountry="US"
              onChange={(val) => {
                setPhone(val);
                if (phoneError) setPhoneError('');
              }}
            />
            {phoneError && (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400">{phoneError}</p>
            )}
          </div>
          <div>
            <Label htmlFor="role">Your role</Label>
            <Select
              id="role"
              value={role}
              onValueChange={(v) => { setRole(v); if (roleError) setRoleError(''); }}
            >
              <SelectTrigger className="block w-full rounded-md bg-white dark:bg-gray-700/80 border border-gray-300 dark:border-gray-600 py-2 px-3.5 text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:ring-2 focus:ring-inset focus:ring-purple-500 sm:text-sm sm:leading-6">
                <SelectValue placeholder="Select your role" />
              </SelectTrigger>
              <SelectContent>
              {getRolesForSelect().map((r) => (
                  <SelectItem key={r.value} value={r.label}>{r.label}</SelectItem>
                ))}
                {/* 'Other' is included in data as label */}
              </SelectContent>
            </Select>
            {role === 'Other' && (
              <div className="mt-3">
                <Label htmlFor="other-role">Please specify</Label>
                <input
                  id="other-role"
                  name="other-role"
                  type="text"
                  onInput={(e) => { setOtherRole(e.target.value); if (roleError) setRoleError(''); }}
                  className="block w-full rounded-md bg-white dark:bg-gray-700/80 border border-gray-300 dark:border-gray-600 py-2 px-3.5 text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-500 sm:text-sm sm:leading-6"
                />
              </div>
            )}
            {roleError && (
              <p className="mt-1 text-xs text-red-600 dark:text-red-400">{roleError}</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-semibold leading-6 text-gray-900 dark:text-white">
            Password
          </label>
          <div className="mt-2 relative">
            <input
              id="password"
              name="password"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              required
              onInput={onInputChange}
              placeholder="Enter password"
              className="block w-full rounded-md bg-white dark:bg-gray-700/80 border border-gray-300 dark:border-gray-600 py-2 px-3.5 text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-500 sm:text-sm sm:leading-6"
            />
            <button
              type="button"
              onClick={togglePasswordVisibility}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              {showPassword ? (
                <EyeOff className="h-5 w-5" aria-hidden="true" />
              ) : (
                <Eye className="h-5 w-5" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>

        <div>
          <label htmlFor="confirm-password" className="block text-sm font-semibold leading-6 text-gray-900 dark:text-white">
            Confirm password
          </label>
          <div className="mt-2 relative">
            <input
              id="confirm-password"
              name="confirm-password"
              type={showConfirmPassword ? "text" : "password"}
              autoComplete="new-password"
              required
              onInput={onInputChange}
              placeholder="Confirm password"
              className="block w-full rounded-md bg-white dark:bg-gray-700/80 border border-gray-300 dark:border-gray-600 py-2 px-3.5 text-gray-900 dark:text-white shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-purple-500 sm:text-sm sm:leading-6"
            />
            <button
              type="button"
              onClick={toggleConfirmPasswordVisibility}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              {showConfirmPassword ? (
                <EyeOff className="h-5 w-5" aria-hidden="true" />
              ) : (
                <Eye className="h-5 w-5" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>

        {/* Terms and Privacy Policy Checkbox */}
        <div className="flex items-start">
          <input
            id="terms-agreement"
            name="terms-agreement"
            type="checkbox"
            required
            checked={agreedToTerms}
            onChange={(e) => setAgreedToTerms(e.target.checked)}
            className="h-4 w-4 mt-1 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-purple-600 focus:ring-purple-500 focus:ring-offset-0"
          />
          <label htmlFor="terms-agreement" className="ml-3 block text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
            I have read and agree to the{' '}
            <Link
              to="/tos"
              state={{ from: '/signup' }}
              className="text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 underline font-medium"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms of Service
            </Link>
            {' '}and{' '}
            <Link 
              to="/privacy" 
              state={{ from: '/signup' }}
              className="text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-300 underline font-medium"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </Link>
          </label>
        </div>

        <div>
          <button
            type="submit"
            disabled={(function(){
              const phoneDigits = String(phone?.national || '').replace(/[^0-9]/g, '');
              const phoneValid = phoneDigits.length === 10;
              const roleValid = role === 'Other' ? (otherRole || '').trim().length > 0 : !!role;
              return isLoading || !agreedToTerms || !phoneValid || !roleValid;
            })()}
            className={`flex w-full justify-center items-center rounded-md bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 active:from-purple-800 active:to-indigo-800 px-3.5 py-3 text-sm font-semibold text-white shadow-md hover:shadow-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600 transition-all duration-200 transform hover:-translate-y-0.5 active:translate-y-0 ${(() => { const phoneDigits = String(phone?.national || '').replace(/[^0-9]/g, ''); const phoneValid = phoneDigits.length === 10; const roleValid = role === 'Other' ? (otherRole || '').trim().length > 0 : !!role; return (isLoading || !agreedToTerms || !phoneValid || !roleValid) ? 'opacity-50 cursor-not-allowed' : ''; })()}`}
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <ButtonSpinner color="white" className="mr-2" />
                <span>Creating account...</span>
              </div>
            ) : (
              <span>Create account</span>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}